import { Space, Button, Segmented, Select } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  StrikethroughOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { FontStyle, TextAlignment, type IVariableFormatting } from '../types';
import { SuperscriptIcon, SubscriptIcon } from './icons';
import {
  DEFAULT_FORMATTING,
  FONT_FAMILY_OPTIONS,
  FONT_SIZE_OPTIONS,
} from '../utils/constants';

export interface IProps {
  formatting: IVariableFormatting;
  onFormattingChange: (formatting: IVariableFormatting) => void;
}

const FormattingToolbar: React.FC<IProps> = ({
  formatting,
  onFormattingChange,
}) => {
  const toggleFormat = (key: keyof Pick<IVariableFormatting, FontStyle>) => {
    onFormattingChange({ ...formatting, [key]: !formatting[key] });
  };

  const handleAlignmentChange = (value: string | number) => {
    onFormattingChange({ ...formatting, alignment: value as TextAlignment });
  };

  const handleFontFamilyChange = (value: string) => {
    onFormattingChange({
      ...formatting,
      fontFamily: value || DEFAULT_FORMATTING.fontFamily,
    });
  };

  const handleFontSizeChange = (value: string) => {
    onFormattingChange({
      ...formatting,
      fontSize: value || DEFAULT_FORMATTING.fontSize,
    });
  };

  return (
    <Container>
      <Space wrap>
        <FormatButton
          icon={<BoldOutlined />}
          $active={formatting.bold}
          onClick={() => toggleFormat(FontStyle.Bold)}
        />
        <FormatButton
          icon={<ItalicOutlined />}
          $active={formatting.italic}
          onClick={() => toggleFormat(FontStyle.Italic)}
        />
        <FormatButton
          icon={<UnderlineOutlined />}
          $active={formatting.underline}
          onClick={() => toggleFormat(FontStyle.Underline)}
        />
        <FormatButton
          icon={<StrikethroughOutlined />}
          $active={formatting.strikethrough}
          onClick={() => toggleFormat(FontStyle.Strikethrough)}
        />
        <FormatButton
          icon={<SuperscriptIcon />}
          $active={formatting.superscript}
          onClick={() => toggleFormat(FontStyle.Superscript)}
        />
        <FormatButton
          icon={<SubscriptIcon />}
          $active={formatting.subscript}
          onClick={() => toggleFormat(FontStyle.Subscript)}
        />
      </Space>
      <Segmented
        value={formatting.alignment}
        onChange={handleAlignmentChange}
        options={[
          { value: TextAlignment.Left, icon: <AlignLeftOutlined /> },
          { value: TextAlignment.Center, icon: <AlignCenterOutlined /> },
          { value: TextAlignment.Right, icon: <AlignRightOutlined /> },
        ]}
      />

      <Select
        value={formatting.fontFamily || ''}
        onChange={handleFontFamilyChange}
        options={FONT_FAMILY_OPTIONS}
        style={{ width: 150 }}
        placeholder="Font"
      />

      <Select
        value={formatting.fontSize || ''}
        onChange={handleFontSizeChange}
        options={FONT_SIZE_OPTIONS}
        style={{ width: 100 }}
        placeholder="Size"
      />
    </Container>
  );
};

export default FormattingToolbar;

const Container = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const FormatButton = styled(Button)<{ $active: boolean }>`
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.white : theme.colors.textPrimary};
  border-color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.border};

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.primaryHover : theme.colors.cardHover} !important;
    color: ${({ $active, theme }) =>
      $active ? theme.colors.white : theme.colors.textPrimary} !important;
  }
`;
